import type { DefaultTemplateRootProps, RepeaterFunctionInvoker, RepeaterRow } from 'Components/types';
import type { ArrayCallback, ArrayPredicate } from 'Core/types';
import createRepeaterComponents from 'Utils/repeater';

import Vehicle, { type VehicleData } from './vehicle';

export default class VehicleCollection {
  static empty = new VehicleCollection([]);

  _vehicles: Vehicle[];

  constructor(vehicles: Vehicle[]) {
    this._vehicles = vehicles;
  }

  get serializable(): VehicleData[] {
    return this._vehicles.map((v) => v.serializable);
  }

  get size(): number {
    return this._vehicles.length;
  }

  get last(): Vehicle | undefined {
    return this._vehicles[this._vehicles.length - 1];
  }

  concat(value: Vehicle | VehicleCollection): VehicleCollection {
    if (value instanceof VehicleCollection) {
      return new VehicleCollection(this._vehicles.concat(value._vehicles.filter((v) => !this.includes(v))));
    }
    return this.includes(value) ? this : new VehicleCollection(this._vehicles.concat(value));
  }

  includes(vehicle: Vehicle): boolean {
    return this._vehicles.some(vehicle.equals);
  }

  find(predicate: ArrayPredicate<Vehicle>): Vehicle | undefined {
    return this._vehicles.find(predicate);
  }

  map<T>(callback: ArrayCallback<Vehicle, T>): T[] {
    return this._vehicles.map(callback);
  }

  remove(vehicle: Vehicle): VehicleCollection {
    return new VehicleCollection(this._vehicles.filter((v) => v !== vehicle));
  }

  replace(vehicleToReplace: Vehicle, newVehicle: Vehicle): VehicleCollection {
    return new VehicleCollection(this._vehicles.map((v) => (v === vehicleToReplace ? newVehicle : v)));
  }

  reverse(): Vehicle[] {
    return this._vehicles.reverse();
  }

  repeaterComponents<
    Params,
    RootProps extends object = DefaultTemplateRootProps,
    RootElement extends HTMLElement = HTMLElement,
  >(
    Component: RepeaterRow<Params, Vehicle, 'vehicle', RootProps, RootElement>,
  ): RepeaterFunctionInvoker<Params, RootProps, RootElement> {
    return createRepeaterComponents(this._vehicles, 'vehicle', Component, (vehicle) => vehicle.key);
  }
}
